<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3';
import { computed } from 'vue';
import { useProjectProgress } from '@/composables/useProjectProgress';
import AppLayout from '@/layouts/AppLayout.vue';
import * as projectRoutes from '@/routes/projects';
import { importMethod } from '@/routes/projects';
import * as articlesRoutes from '@/routes/projects/articles';
import * as dataRoutes from '@/routes/projects/data';
import type { Project } from '@/types';

interface DocumentSummary {
    id: string;
    title: string;
    updated_at: string | null;
}

interface Props {
    project: Project;
    document: DocumentSummary | null;
}

const props = defineProps<Props>();

defineOptions({
    layout: (props: { project: Project }) => [
        AppLayout,
        {
            breadcrumbs: [
                { title: 'Projets', href: '/projects' },
                {
                    title: props.project.name,
                    href: `/projects/${props.project.id}`,
                },
            ],
        },
    ],
});

const { steps, screened, nextStepKey } = useProjectProgress(
    () => props.project,
);

const stepHrefs = computed(() => ({
    1: articlesRoutes.index(props.project.id).url,
    2: articlesRoutes.index(props.project.id).url,
    3: articlesRoutes.index(props.project.id).url,
    4: dataRoutes.index(props.project.id).url,
    5: projectRoutes.editor(props.project.id).url,
}));

const nextActionMap = {
    import: {
        title: 'Importez vos premiers articles',
        description: 'Depuis Zotero, des fichiers RIS/BibTeX ou manuellement.',
        label: 'Importer des articles',
        href: () => importMethod(props.project.id).url,
    },
    screen: {
        title: 'Continuez le screening',
        description: `Articles en attente de décision.`,
        label: 'Reprendre le screening',
        href: () => articlesRoutes.index(props.project.id).url,
    },
    extract: {
        title: 'Extrayez les données',
        description: "Des articles inclus sont prêts pour l'extraction.",
        label: "Gérer la grille d'extraction",
        href: () => dataRoutes.index(props.project.id).url,
    },
    write: {
        title: 'Rédigez votre synthèse',
        description: "Screening terminé. Ouvrez l'éditeur pour rédiger.",
        label: "Ouvrir l'éditeur",
        href: () => projectRoutes.editor(props.project.id).url,
    },
} as const;

const nextAction = computed(() => nextActionMap[nextStepKey.value]);

function stepState(step: { done: boolean; current: boolean }) {
    if (step.done) {
        return 'Terminé';
    }

    if (step.current) {
        return 'En cours';
    }

    return 'À venir';
}

function memberInitial(name?: string | null) {
    return name?.charAt(0)?.toUpperCase() ?? '?';
}
</script>

<template>
    <Head :title="project.name" />

    <div class="page-main">
        <div class="page-inner">
            <!-- Page Header -->
            <header class="page-header">
                <div class="page-header-row">
                    <div>
                        <div class="page-eyebrow">Projet</div>
                        <h1 class="page-h1">{{ project.name }}</h1>
                    </div>
                    <div class="page-header-actions">
                        <Link
                            v-if="(project.papers_count ?? 0) === 0"
                            :href="importMethod(project.id).url"
                            class="btn-primary"
                        >
                            Importer des articles
                        </Link>
                        <Link
                            :href="projectRoutes.settings(project.id).url"
                            class="btn-ghost"
                        >
                            Configurer
                        </Link>
                        <Link
                            :href="projectRoutes.editor(project.id).url"
                            class="btn-ghost"
                        >
                            Ouvrir la rédaction
                        </Link>
                    </div>
                </div>
                <p class="page-lead">
                    {{
                        project.description ||
                        'Aucune description renseignée pour ce projet.'
                    }}
                </p>
            </header>

            <!-- Stats rapides -->
            <section class="overview-stats" data-tour="overview-stats">
                <div class="stat-tile">
                    <div class="stat-value">
                        {{ project.papers_count ?? 0 }}
                    </div>
                    <div class="stat-label">Articles identifiés</div>
                </div>
                <div class="stat-tile">
                    <div class="stat-value">{{ screened }}</div>
                    <div class="stat-label">Articles sélectionnés</div>
                </div>
                <div class="stat-tile">
                    <div class="stat-value">
                        {{ project.papers_included_count ?? 0 }}
                    </div>
                    <div class="stat-label">Articles inclus</div>
                </div>
                <div class="stat-tile">
                    <div class="stat-value">
                        {{ project.papers_pending_count ?? 0 }}
                    </div>
                    <div class="stat-label">À examiner</div>
                </div>
            </section>

            <!-- Avancement de la revue (mini-PRISMA) -->
            <section
                class="page-block overview-progress"
                data-tour="overview-progress"
            >
                <div class="block-header">
                    <h2 class="block-title">Avancement de la revue</h2>
                    <Link
                        :href="projectRoutes.prismaStats(project.id).url"
                        class="link-action"
                    >
                        Voir le diagramme PRISMA
                    </Link>
                </div>
                <ol class="mini-prisma">
                    <li
                        v-for="step in steps"
                        :key="step.num"
                        class="mini-step"
                        :class="{
                            'is-done': step.done,
                            'is-current': step.current,
                        }"
                    >
                        <Link
                            :href="
                                stepHrefs[step.num as keyof typeof stepHrefs]
                            "
                            class="flex w-full items-start gap-3 no-underline"
                        >
                            <div class="mini-step-num">{{ step.num }}</div>
                            <div class="mini-step-body">
                                <div class="mini-step-title">
                                    {{ step.title }}
                                </div>
                                <div class="mini-step-meta">
                                    {{ step.meta }}
                                </div>
                                <div
                                    v-if="step.progress !== null && !step.done"
                                    class="progress"
                                >
                                    <span
                                        class="progress-bar"
                                        :style="{ width: `${step.progress}%` }"
                                    ></span>
                                </div>
                            </div>
                            <div class="mini-step-state">
                                {{ stepState(step) }}
                            </div>
                        </Link>
                    </li>
                </ol>
            </section>

            <!-- Colonnes : Documents + Prochaine action -->
            <div class="page-cols">
                <!-- Documents du projet -->
                <section class="page-block">
                    <div class="block-header">
                        <h2 class="block-title">Documents du projet</h2>
                        <Link
                            :href="projectRoutes.editor(project.id).url"
                            class="link-action"
                        >
                            Ouvrir l'éditeur
                        </Link>
                    </div>
                    <ul v-if="document" class="doc-list">
                        <li class="doc-row">
                            <div class="doc-row-main">
                                <span
                                    class="doc-icon"
                                    aria-hidden="true"
                                ></span>
                                <div>
                                    <div class="doc-row-title">
                                        {{ document.title }}
                                    </div>
                                    <div
                                        v-if="document.updated_at"
                                        class="doc-row-meta"
                                    >
                                        Modifié {{ document.updated_at }}
                                    </div>
                                </div>
                            </div>
                            <span class="doc-row-tag">En cours</span>
                        </li>
                    </ul>
                    <div v-else class="doc-list">
                        <p
                            style="
                                font-size: 12px;
                                color: var(--g-500);
                                padding: 10px 0;
                            "
                        >
                            Aucun document pour l'instant.
                        </p>
                    </div>
                </section>

                <!-- Prochaine action -->
                <section class="page-block">
                    <div class="block-header">
                        <h2 class="block-title">Prochaine action</h2>
                        <Link
                            :href="projectRoutes.activity(project.id).url"
                            class="link-action"
                        >
                            Voir l'activité
                        </Link>
                    </div>
                    <div style="padding: 4px 0">
                        <p
                            style="
                                font-size: 13px;
                                font-weight: 600;
                                margin-bottom: 4px;
                            "
                        >
                            {{ nextAction.title }}
                        </p>
                        <p
                            style="
                                font-size: 12px;
                                color: var(--g-500);
                                margin-bottom: 12px;
                            "
                        >
                            {{ nextAction.description }}
                        </p>
                        <Link
                            :href="nextAction.href()"
                            class="btn-ghost"
                            style="font-size: 12px; display: inline-flex"
                        >
                            {{ nextAction.label }} →
                        </Link>
                    </div>
                </section>
            </div>

            <!-- Colonne : Personnes -->
            <div class="page-cols">
                <section class="page-block">
                    <div class="block-header">
                        <h2 class="block-title">Personnes</h2>
                        <Link
                            :href="projectRoutes.settings(project.id).url"
                            class="link-action"
                        >
                            Gérer
                        </Link>
                    </div>
                    <ul
                        v-if="project.members && project.members.length > 0"
                        class="people-list"
                    >
                        <li
                            v-for="member in project.members"
                            :key="member.id"
                            class="person-row"
                        >
                            <span class="avatar">{{
                                memberInitial(member.user?.name)
                            }}</span>
                            <div>
                                <div class="person-name">
                                    {{ member.user?.name ?? '—' }}
                                </div>
                                <div class="person-role">{{ member.role }}</div>
                            </div>
                        </li>
                    </ul>
                    <ul v-else class="people-list">
                        <li class="person-row">
                            <span class="avatar">{{
                                memberInitial(project.owner.name)
                            }}</span>
                            <div>
                                <div class="person-name">
                                    {{ project.owner.name }}
                                </div>
                                <div class="person-role">Auteur principal</div>
                            </div>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </div>
</template>
